import maplibregl, { type Map as MapLibreMap, type Marker as MapLibreMarker } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { hasPhotoLocation } from '../../domain/trail';
import type { RestDay } from '../../domain/trail';
import type { Activity, Photo, RecordedRoute } from '../../schemas';
import { activityIdSchema } from '../../schemas';
import { MapCanvas, PaperGrain } from '../../styles';
import {
  addRecordedRoute,
  clusterPhotoMarkersOnScreen,
  clusterPhotosByLocation,
  focusActivity,
  focusPhotos,
  focusWholeRoute,
  markerDensityTiers,
  nzView,
  photoClusterExpansionCamera,
  setElevationLocation,
  setSelectedRoute,
  terrainStyle,
  type LocatedPhoto,
  type PhotoMarkerCluster,
} from './mapLogic';

export function TrailMap({
  route,
  photos,
  selectedDayDate,
  selectedActivity,
  selectedRestDay,
  selectedDayPhotos,
  highlightedPhotoId,
  elevationLocation,
  isThreeDimensional,
  wholeRouteRequest,
  onActivitySelect,
  onPhotoOpen,
}: {
  route: RecordedRoute | null;
  photos: Photo[];
  selectedDayDate: string | null;
  selectedActivity: Activity | null;
  selectedRestDay: RestDay | null;
  selectedDayPhotos: Photo[];
  highlightedPhotoId: string | null;
  elevationLocation: [number, number] | null;
  isThreeDimensional: boolean;
  wholeRouteRequest: number;
  onActivitySelect: (activityId: string | null) => void;
  onPhotoOpen: (photo: Photo) => void;
}) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const onActivitySelectRef = useRef(onActivitySelect);
  const onPhotoOpenRef = useRef(onPhotoOpen);
  const [styleReady, setStyleReady] = useState(false);
  const [routeReady, setRouteReady] = useState(false);
  const [photoMarkerElements] = useState(() => new Map<string, HTMLButtonElement>());
  const previousHighlightedPhotoId = useRef<string | null>(null);
  const highlightedPhotoIdRef = useRef<string | null>(null);
  const lastWholeRouteRequest = useRef(wholeRouteRequest);
  const viewInitialized = useRef(false);

  useEffect(() => {
    onActivitySelectRef.current = onActivitySelect;
    onPhotoOpenRef.current = onPhotoOpen;
  });

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: terrainStyle,
      ...nzView,
      attributionControl: false,
      maxPitch: 76,
      renderWorldCopies: window.matchMedia('(max-width: 700px)').matches,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');
    map.once('style.load', () => setStyleReady(true));

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady || !route || map.getSource('recorded-route')) return;
    addRecordedRoute(map, route);
    setRouteReady(true);
    map.setTerrain({ source: 'dem', exaggeration: 1.55 });
    focusWholeRoute(map, route, window.matchMedia('(max-width: 700px)').matches, false);

    const handleRouteEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const handleRouteLeave = () => {
      map.getCanvas().style.cursor = '';
    };
    const handleMapClick = (event: maplibregl.MapMouseEvent) => {
      const feature = map.queryRenderedFeatures(event.point, { layers: ['recorded-route-hit'] })[0];
      if (!feature) {
        onActivitySelectRef.current(null);
        return;
      }
      const rawId = feature.properties?.activity_id;
      const parsedId = activityIdSchema.safeParse(
        typeof rawId === 'string' || typeof rawId === 'number' ? String(rawId) : rawId,
      );
      if (parsedId.success) onActivitySelectRef.current(parsedId.data);
    };
    map.on('mouseenter', 'recorded-route-hit', handleRouteEnter);
    map.on('mouseleave', 'recorded-route-hit', handleRouteLeave);
    map.on('click', handleMapClick);

    return () => {
      map.off('mouseenter', 'recorded-route-hit', handleRouteEnter);
      map.off('mouseleave', 'recorded-route-hit', handleRouteLeave);
      map.off('click', handleMapClick);
    };
  }, [route, styleReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !route || !routeReady) return;
    setSelectedRoute(map, selectedActivity?.activity_id ?? null, selectedDayDate);
    setElevationLocation(map, null);
    if (selectedActivity) {
      focusActivity(
        map,
        route,
        selectedActivity.activity_id,
        window.matchMedia('(max-width: 700px)').matches,
      );
    } else if (selectedRestDay) {
      focusPhotos(map, selectedDayPhotos, window.matchMedia('(max-width: 700px)').matches);
    }
  }, [route, routeReady, selectedActivity, selectedDayDate, selectedDayPhotos, selectedRestDay]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !route || !routeReady || wholeRouteRequest === lastWholeRouteRequest.current) return;
    lastWholeRouteRequest.current = wholeRouteRequest;
    focusWholeRoute(map, route, window.matchMedia('(max-width: 700px)').matches);
  }, [route, routeReady, wholeRouteRequest]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    map.setTerrain(isThreeDimensional ? { source: 'dem', exaggeration: 1.55 } : null);
    if (!viewInitialized.current) {
      viewInitialized.current = true;
      if (isThreeDimensional) return;
    }
    map.easeTo({
      pitch: isThreeDimensional ? nzView.pitch : 0,
      bearing: isThreeDimensional ? nzView.bearing : 0,
      duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 900,
    });
  }, [isThreeDimensional, styleReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !routeReady) return;
    setElevationLocation(map, elevationLocation);
  }, [elevationLocation, routeReady]);

  useEffect(() => {
    const previousElement = previousHighlightedPhotoId.current
      ? photoMarkerElements.get(previousHighlightedPhotoId.current)
      : undefined;
    const nextElement = highlightedPhotoId ? photoMarkerElements.get(highlightedPhotoId) : undefined;
    if (previousElement && previousElement !== nextElement) previousElement.dataset.highlighted = 'false';
    if (nextElement) nextElement.dataset.highlighted = 'true';
    previousHighlightedPhotoId.current = highlightedPhotoId;
    highlightedPhotoIdRef.current = highlightedPhotoId;
  }, [highlightedPhotoId, photoMarkerElements]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !routeReady || !photos.length) return;
    const photoClusters = clusterPhotosByLocation(photos.filter(hasPhotoLocation));
    const clustersByDate = new Map<string, PhotoMarkerCluster[]>();
    for (const cluster of photoClusters) {
      const dayClusters = clustersByDate.get(cluster.anchor.capture_date) ?? [];
      dayClusters.push(cluster);
      clustersByDate.set(cluster.anchor.capture_date, dayClusters);
    }
    const densityTiers = new Map<string, 0 | 1 | 2>();
    for (const dayClusters of clustersByDate.values()) {
      for (const [clusterId, tier] of markerDensityTiers(dayClusters, 1, 3)) {
        densityTiers.set(clusterId, tier);
      }
    }
    let renderedMarkers: Array<{ marker: MapLibreMarker; photoIds: string[] }> = [];

    const clearRenderedMarkers = () => {
      for (const { marker, photoIds } of renderedMarkers) {
        marker.remove();
        for (const photoId of photoIds) photoMarkerElements.delete(photoId);
      }
      renderedMarkers = [];
    };

    const renderMarkers = () => {
      clearRenderedMarkers();
      const zoom = map.getZoom();
      const maximumTier = zoom >= 7.4 ? 2 : zoom >= 6.1 ? 1 : 0;
      const visiblePhotoMarkers = photoClusters.filter(
        (cluster) => (densityTiers.get(cluster.id) ?? 2) <= maximumTier,
      );
      const clusterRadiusPx = window.matchMedia('(max-width: 700px)').matches ? 40 : 46;
      const visualClusters = clusterPhotoMarkersOnScreen(map, visiblePhotoMarkers, clusterRadiusPx);

      renderedMarkers = visualClusters.flatMap((visualCluster) => {
        const orderedVisualCluster = selectedDayDate
          ? [
              ...visualCluster.filter((cluster) => cluster.anchor.capture_date === selectedDayDate),
              ...visualCluster.filter((cluster) => cluster.anchor.capture_date !== selectedDayDate),
            ]
          : visualCluster;
        const frontLocation = orderedVisualCluster[0];
        if (!frontLocation) return [];
        const photo = frontLocation.anchor;
        const photoIds = orderedVisualCluster.flatMap((cluster) =>
          cluster.photos.map((clusterPhoto) => clusterPhoto.id),
        );
        const expansionCamera =
          orderedVisualCluster.length > 1
            ? photoClusterExpansionCamera(map, orderedVisualCluster, clusterRadiusPx)
            : null;
        const markerKind = expansionCamera ? 'cluster' : photoIds.length > 1 ? 'set' : 'photo';
        const isCurrentDay = orderedVisualCluster.some(
          (cluster) => cluster.anchor.capture_date === selectedDayDate,
        );
        const element = createPhotoMarkerElement({
          visualCluster,
          orderedVisualCluster,
          photo,
          photoIds,
          markerKind,
          isCurrentDay,
          selectionActive: selectedDayDate !== null,
        });
        // oxlint-disable-next-line unicorn/prefer-add-event-listener -- each disposable marker owns one handler
        element.onclick = (event) => {
          event.stopPropagation();
          if (expansionCamera) {
            map.easeTo({
              center: expansionCamera.center,
              zoom: expansionCamera.zoom,
              duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 700,
            });
          } else {
            onPhotoOpenRef.current(photo);
          }
        };
        const marker = new maplibregl.Marker({
          element,
          anchor: 'bottom',
          pitchAlignment: 'viewport',
          rotationAlignment: 'viewport',
          subpixelPositioning: true,
        })
          .setLngLat([photo.lon, photo.lat])
          .addTo(map);
        for (const photoId of photoIds) photoMarkerElements.set(photoId, element);
        return [{ marker, photoIds }];
      });

      const highlightedElement = highlightedPhotoIdRef.current
        ? photoMarkerElements.get(highlightedPhotoIdRef.current)
        : undefined;
      if (highlightedElement) highlightedElement.dataset.highlighted = 'true';
    };

    renderMarkers();
    map.on('zoomend', renderMarkers);
    map.on('resize', renderMarkers);
    return () => {
      map.off('zoomend', renderMarkers);
      map.off('resize', renderMarkers);
      clearRenderedMarkers();
    };
  }, [photoMarkerElements, photos, routeReady, selectedDayDate]);

  return (
    <>
      <MapCanvas
        ref={mapContainer}
        aria-label="Interactive 3D map of the recorded South Island Te Araroa walk from Ship Cove to Bluff"
      />
      <PaperGrain aria-hidden="true" />
    </>
  );
}

function createPhotoMarkerElement({
  visualCluster,
  orderedVisualCluster,
  photo,
  photoIds,
  markerKind,
  isCurrentDay,
  selectionActive,
}: {
  visualCluster: PhotoMarkerCluster[];
  orderedVisualCluster: PhotoMarkerCluster[];
  photo: LocatedPhoto;
  photoIds: string[];
  markerKind: 'cluster' | 'set' | 'photo';
  isCurrentDay: boolean;
  selectionActive: boolean;
}) {
  const element = document.createElement('button');
  element.type = 'button';
  element.className = 'trail-photo-marker';
  element.dataset.selected = String(isCurrentDay);
  element.dataset.selectionActive = String(selectionActive);
  element.dataset.markerKind = markerKind;
  element.dataset.highlighted = 'false';
  const displayedLocations =
    markerKind === 'cluster' ? orderedVisualCluster.slice(0, 3) : [orderedVisualCluster[0]!];
  element.dataset.stackSize = String(displayedLocations.length);
  element.setAttribute(
    'aria-label',
    markerKind === 'cluster'
      ? `Zoom in to reveal ${visualCluster.length} photo locations`
      : markerKind === 'set'
        ? `Open the first of ${photoIds.length} photos from this location`
        : `Open photo from this location on ${photo.capture_date}`,
  );

  for (const [stackIndex, location] of displayedLocations.entries()) {
    const frame = document.createElement('span');
    frame.className = 'trail-photo-marker-frame';
    frame.dataset.stackIndex = String(stackIndex);
    const image = document.createElement('img');
    image.src = location.anchor.thumbnail_path;
    image.alt = '';
    image.loading = 'lazy';
    image.decoding = 'async';
    frame.append(image);
    element.append(frame);
  }
  if (markerKind === 'set') {
    const signifier = document.createElement('span');
    signifier.className = 'trail-photo-marker-photo-count';
    signifier.setAttribute('aria-hidden', 'true');
    signifier.textContent = String(photoIds.length);
    element.append(signifier);
  }
  return element;
}
